import { type Matrix, inv, matrix, mean, multiply, subtract, transpose, zeros } from "mathjs";

import type { Point } from "../types";

const settings = {
  /**
   * If a point set's linear regression has a correlation coefficient
   * greater than this value, we represent the point set with a line.
   */
  linearErrorThreshold: 0.995,

  /**
   * Like linearErrorThreshold, but for small lines.  "Small" is defined by
   * smallLineDistanceThreshold.
   */
  linearErrorThresholdSmall: 0.85,

  /**
   * Point sets that span less than this length are considered "small", and
   * are subject to a different correlation coefficient for linear
   * regression.  This value is in feet.
   *
   * We differentiate between large and small point sets because small
   * point sets are likely to have fluctuations that are large relative
   * to the number of points.
   */
  smallLineDistanceThreshold: 3.0,

  /**
   * Correlation coefficient threshold to accept a quadratic fit.
   */
  quadErrorThreshold: 0.996,

  /**
   * Correlation coefficient threshold to accept a cubic fit.
   */
  cubicErrorThreshold: 0.9,

  /**
   * Curve-fitting involves an iterative process to better estimate t-values
   * from distance measurements.  This is the number of iterations.
   */
  fittingIterations: 10,

  /**
   * The above fitting routine generates a point set.  This is the number
   * of points.
   */
  fittingPoints: 1000,

  /**
   * To generate dribble squiggles and other sinusoids, we generate many
   * points at equally spaced points on a curve.
   * This is the number of points to generate.
   */
  sinusoidPoints: 1000,

  /**
   * The wavelength of a sinusoid in feet.
   */
  sinusoidWavelength: 0.9,

  /**
   * The amplitude of a sinusoid in feet.
   */
  sinusoidAmplitude: 1.1,

  /**
   * The T-value for a cubic's second manipulation point.
   */
  cubicSecondManipulationPointTValue: 0.25,

  /**
   * The T-value for a cubic's third manipulation point.
   */
  cubicThirdManipulationPointTValue: 0.75,

  /**
   * The T-value for a quadratic's second manipulation point.
   */
  quadSecondManipulationPointTValue: 0.5,
} as const;

/**
 * 🧮 MATH: BÉZIER CURVE (MATRIX FORM)
 * This function calculates points on a Bézier curve using its matrix representation.
 * The formula is:
 * P = T * M * C
 * Where:
 * P = [x, y] points on the curve
 * T = The "time" matrix (powers of t: [t^3, t^2, t, 1])
 * M = The Bézier basis matrix (a constant matrix for each curve degree)
 * C = The control points matrix
 */
const pointsOnBezierCurveWithBezierMatrix = (
  timeMatrix: Matrix,
  bezierMatrix: Matrix,
  controlPoints: Point[]
): Point[] => {
  const yControlPoints = matrix(zeros(controlPoints.length, 1));
  const xControlPoints = matrix(zeros(controlPoints.length, 1));

  // Separate X and Y control points into their own matrices
  for (let i = 0; i < controlPoints.length; i++) {
    xControlPoints.set([i, 0], controlPoints[i]?.x ?? 0);
    yControlPoints.set([i, 0], controlPoints[i]?.y ?? 0);
  }

  const returnPoints: Point[] = [];

  // Calculate all X and Y points in parallel using matrix multiplication
  // P_y = T * M * C_y
  const yPointsOnCurve = multiply(multiply(timeMatrix, bezierMatrix), yControlPoints);
  // P_x = T * M * C_x
  const xPointsOnCurve = multiply(multiply(timeMatrix, bezierMatrix), xControlPoints);

  // Re-combine X and Y values into Point objects
  for (let i = 0; i < (yPointsOnCurve.size()[0] ?? 0); i++) {
    returnPoints.push({
      x: xPointsOnCurve.get([i, 0]) as number,
      y: yPointsOnCurve.get([i, 0]) as number,
    });
  }
  return returnPoints;
};

// --- Helper functions for specific Bézier curve degrees ---

const pointsOnLinearCurve = (tValues: Matrix, controlPoints: Point[]): Point[] => {
  // T matrix for linear: [t, 1]
  const timeMatrix = matrix(zeros(tValues.size()[0] ?? 1, 2));
  for (let i = 0; i < (tValues.size()[0] ?? 0); i++) {
    timeMatrix.set([i, 0], tValues.get([i, 0]));
    timeMatrix.set([i, 1], 1);
  }

  // M matrix (Bézier basis) for linear
  const linearBezierMatrix = matrix([
    [-1, 1],
    [1, 0],
  ]);

  return pointsOnBezierCurveWithBezierMatrix(timeMatrix, linearBezierMatrix, controlPoints);
};

const pointsOnQuadraticCurve = (tValues: Matrix, controlPoints: Point[]): Point[] => {
  // T matrix for quadratic: [t^2, t, 1]
  const timeMatrix = matrix(zeros(tValues.size()[0] ?? 1, 3));
  for (let i = 0; i < (tValues.size()[0] ?? 0); i++) {
    const t = tValues.get([i, 0]) as number;
    timeMatrix.set([i, 0], t * t);
    timeMatrix.set([i, 1], t);
    timeMatrix.set([i, 2], 1);
  }

  // M matrix (Bézier basis) for quadratic
  const quadraticBezierMatrix = matrix([
    [1, -2, 1],
    [-2, 2, 0],
    [1, 0, 0],
  ]);

  return pointsOnBezierCurveWithBezierMatrix(timeMatrix, quadraticBezierMatrix, controlPoints);
};

const pointsOnCubicCurve = (tValues: Matrix, controlPoints: Point[]): Point[] => {
  // T matrix for cubic: [t^3, t^2, t, 1]
  const timeMatrix = matrix(zeros(tValues.size()[0] ?? 1, 4));
  for (let i = 0; i < (tValues.size()[0] ?? 0); i++) {
    const t = tValues.get([i, 0]) as number;
    timeMatrix.set([i, 0], t * t * t);
    timeMatrix.set([i, 1], t * t);
    timeMatrix.set([i, 2], t);
    timeMatrix.set([i, 3], 1);
  }

  // M matrix (Bézier basis) for cubic
  const cubicBezierMatrix = matrix([
    [-1, 3, -3, 1],
    [3, -6, 3, 0],
    [-3, 3, 0, 0],
    [1, 0, 0, 0],
  ]);

  return pointsOnBezierCurveWithBezierMatrix(timeMatrix, cubicBezierMatrix, controlPoints);
};

/**
 * A wrapper function that selects the correct degree (linear, quadratic, cubic)
 * based on the number of control points provided.
 */
const pointsOnBezierCurve = (tValues: Matrix, controlPoints: Point[]): Point[] => {
  const returnPoints: Point[] = [];
  switch (controlPoints.length) {
    case 2:
      return pointsOnLinearCurve(tValues, controlPoints);
    case 3:
      return pointsOnQuadraticCurve(tValues, controlPoints);
    case 4:
      return pointsOnCubicCurve(tValues, controlPoints);
    default:
      break;
  }
  return returnPoints;
};

/**
 * 🧮 MATH: COEFFICIENT OF DETERMINATION (R²)
 * Calculates the R-squared value, a statistical measure of how well the
 * fitted Bézier curve (defined by T*M*C) explains the variance in the
 * original data points (xPointsToFit, yPointsToFit).
 *
 * The formula is: R² = 1 - (SS_res / SS_tot)
 * - SS_res (Sum of Squared Residuals): The error between the
 * original points and the fitted curve.
 * - SS_tot (Total Sum of Squares): The variance of the original points.
 *
 * An R² of 1.0 is a perfect fit. An R² of 0.0 means the curve is no
 * better than a horizontal line.
 */
const rSquaredVal = ({
  bezierMatrix,
  timeMatrix,
  xControlPoints,
  xPointsToFit,
  yControlPoints,
  yPointsToFit,
}: {
  xPointsToFit: Matrix;
  yPointsToFit: Matrix;
  yControlPoints: Matrix;
  xControlPoints: Matrix;
  bezierMatrix: Matrix;
  timeMatrix: Matrix;
}): number => {
  // Calculate SS_res (error) for Y and X
  // SS_res_y = (y_original - y_fitted)^T * (y_original - y_fitted)
  // y_fitted = T * M * C_y
  const partialSumErrorY = multiply(
    transpose(subtract(yPointsToFit, multiply(multiply(timeMatrix, bezierMatrix), yControlPoints))),
    subtract(yPointsToFit, multiply(multiply(timeMatrix, bezierMatrix), yControlPoints))
  );
  const partialSumErrorX = multiply(
    transpose(subtract(xPointsToFit, multiply(multiply(timeMatrix, bezierMatrix), xControlPoints))),
    subtract(xPointsToFit, multiply(multiply(timeMatrix, bezierMatrix), xControlPoints))
  );

  // Calculate SS_tot (variance) for Y and X
  const meanY = mean(yPointsToFit);
  const meanX = mean(xPointsToFit);

  const xTotalSquaresDiff = subtract(xPointsToFit, meanX);
  const yTotalSquaresDiff = subtract(yPointsToFit, meanY);

  // SS_tot_x = (x_original - x_mean)^T * (x_original - x_mean)
  const xTotalSumSquares = multiply(transpose(xTotalSquaresDiff as Matrix), xTotalSquaresDiff);
  const yTotalSumSquares = multiply(transpose(yTotalSquaresDiff as Matrix), yTotalSquaresDiff);

  // Combine X and Y errors and variances
  const totalPartialSumError = (partialSumErrorX.get([0, 0]) as number) + (partialSumErrorY.get([0, 0]) as number);
  const totalSumSquares = (xTotalSumSquares.get([0, 0]) as number) + (yTotalSumSquares.get([0, 0]) as number);

  // R² = 1 - (SS_res / SS_tot)
  const R2 = 1 - totalPartialSumError / totalSumSquares;

  return R2;
};

// --- Standard geometry helper functions ---

const euclideanDistance = (p1: Point, p2: Point): number => {
  return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
};

const angleBetweenPoints = (p1: Point, p2: Point): number => {
  return Math.atan2(p2.y - p1.y, p2.x - p1.x);
};

/**
 * 🧮 MATH: TRIGONOMETRY FOR NORMALS (PERPENDICULAR LINES)
 * Calculates a new point that is perpendicular (at a "normal" 90-degree angle)
 * to the direction of the curve at a specific point.
 *
 * It does this by:
 * 1. Finding the angle of the curve's tangent (the direction of travel)
 * using `angleBetweenPoints`.
 * 2. Adding 90° (Math.PI / 2 radians) to that angle to get the normal.
 * 3. Using sine and cosine to find the new [x, y] coordinates at the
 * desired `distanceFromCurve`.
 */
const pointPerpendicularToPointsOnCurve = ({
  realPointsOnCurve,
  indexOfPoint,
  distanceFromCurve,
  aboveCurve,
}: {
  realPointsOnCurve: Point[];
  indexOfPoint: number;
  distanceFromCurve: number;
  aboveCurve: boolean;
}): Point => {
  // Get the tangent (direction) from the current point to the next point
  const slopeIndex = indexOfPoint + 1;
  const currentPoint = realPointsOnCurve[indexOfPoint];
  const slopePoint = realPointsOnCurve[slopeIndex];

  if (!currentPoint || !slopePoint) {
    throw new Error("Invalid index for points on curve.");
  }

  // Get the angle and add 90 degrees (PI/2) to get the perpendicular
  // Add another 180 degrees (PI) if we want the point "below" the curve
  const angle = angleBetweenPoints(currentPoint, slopePoint) + Math.PI / 2 + Math.PI * (aboveCurve ? 0 : 1);

  // Calculate the new point's position using standard trig
  const returnPoint: Point = {
    // x = x_start + distance * cos(angle)
    x: currentPoint.x + distanceFromCurve * Math.cos(angle),
    // y = y_start + distance * sin(angle)
    y: currentPoint.y + distanceFromCurve * Math.sin(angle),
  };

  return returnPoint;
};

/**
 * 🎯 FUNCTION: CREATE SINUSOIDAL (WAVY) CURVES
 * This function takes a set of Bézier curves and converts them into a
 * "wavy" or "squiggle" pattern.
 *
 * It "walks" along the original curve, and at every half-wavelength,
 * it finds a point perpendicular to the curve. It then strings these
 * points together (anchor, perpendicular_point, anchor) to create
 * a new set of quadratic Bézier curves that form a sine wave.
 */
export const sinusodialBezierPathsForBezierPaths = (segmentsOfControlPoints: Point[][]): Point[][] => {
  const returnCurve: Point[][] = [];

  // 1. Generate many t-values (0.0 to 1.0) to create finely-detailed
  //    points along the curve.
  const tValues = matrix(zeros([settings.sinusoidPoints + 1, 1]));
  for (let i = 0; i <= settings.sinusoidPoints; i++) {
    tValues.set([i, 0], i / settings.sinusoidPoints);
  }

  let findPointAboveCurve = true;
  let anchorPoint = segmentsOfControlPoints[0]?.[0]; // Start at the beginning

  if (!anchorPoint) {
    return returnCurve;
  }

  // 2. Iterate through each segment (e.g., if the line was split by a cusp)
  for (let i = 0; i < segmentsOfControlPoints.length; i++) {
    const bezierSubPath = segmentsOfControlPoints[i];

    if (!bezierSubPath) {
      continue;
    }

    // Generate the high-resolution points for this curve segment
    const pointsOnCurve = pointsOnBezierCurve(tValues, bezierSubPath);

    const adjustedDistanceMatrix = matrix(zeros([settings.sinusoidPoints + 1, 1]));
    for (let j = 1; j <= settings.sinusoidPoints; j++) {
      const point1 = pointsOnCurve[j];
      const point2 = pointsOnCurve[j - 1];
      if (!point1 || !point2) {
        continue;
      }
      const adjDistance = euclideanDistance(point1, point2);
      adjustedDistanceMatrix.set([j, 0], adjDistance);
    }

    let midPointIndex = -1;
    let midPointFound = false;

    // 3. "Walk" along the high-resolution points
    for (let k = 0; k < pointsOnCurve.length; k++) {
      const point1 = pointsOnCurve[k];
      if (!point1) {
        continue;
      }
      const distance = euclideanDistance(point1, anchorPoint);

      // 4. Find the "midpoint" of the wave (half-wavelength)
      if (!midPointFound && distance >= settings.sinusoidWavelength / 2) {
        midPointIndex = k;
        midPointFound = true;
      }

      // 5. When we reach a full wavelength...
      if (distance >= settings.sinusoidWavelength) {
        // create a new quadratic segment:
        const sineSegment = [
          anchorPoint, // Start of the wave (P0)
          // The peak of the wave (P1)
          pointPerpendicularToPointsOnCurve({
            realPointsOnCurve: pointsOnCurve,
            indexOfPoint: midPointIndex,
            distanceFromCurve: settings.sinusoidAmplitude,
            aboveCurve: findPointAboveCurve,
          }),
          point1, // End of the wave (P2)
        ];
        midPointFound = false;
        anchorPoint = point1; // The new anchor is the end of this wave
        returnCurve.push(sineSegment);
        findPointAboveCurve = !findPointAboveCurve; // Alternate above/below
      }
    }
  }

  // Add a final straight line segment for any remaining part of the curve
  const lastSubPath = segmentsOfControlPoints[segmentsOfControlPoints.length - 1];
  const lastPoint = lastSubPath?.[lastSubPath.length - 1];
  if (lastPoint) {
    const straightLine = [anchorPoint, lastPoint];
    if (straightLine) {
      returnCurve.push(straightLine);
    }
  }

  return returnCurve;
};

/**
 * 🎯 FUNCTION: CONVERT TO "TOUCHABLE" ANCHORS
 * This function converts standard Bézier control points (which are often
 * OFF the curve) into "anchor" points that lie directly ON the curve.
 * This is useful for a UI where a user might want to drag a point
 * on the curve to manipulate it.
 *
 * - For a Quadratic curve: [P0, P1, P2] -> [P0, P(t=0.5), P2]
 * - For a Cubic curve: [P0, P1, P2, P3] -> [P0, P(t=0.25), P(t=0.75), P3]
 */
export const touchableAnchorPointPathsForBezierPaths = (segmentsOfControlPoints: Point[][]): Point[][] => {
  const segmentsOfAnchorPoints: Point[][] = [];
  for (let i = 0; i < segmentsOfControlPoints.length; i++) {
    const segmentControlPoints = segmentsOfControlPoints[i];
    switch (segmentControlPoints?.length) {
      case 2: // Linear
        segmentsOfAnchorPoints.push(segmentControlPoints);
        break;
      case 3: // Quadratic
        segmentsOfAnchorPoints.push([
          segmentControlPoints[0]!,
          pointsOnBezierCurve(
            // t-value for the single anchor point on a quadratic curve
            matrix([[settings.quadSecondManipulationPointTValue]]),
            segmentControlPoints
          )[0]!,
          segmentControlPoints[2]!,
        ]);
        break;
      case 4: {
        // Cubic
        const calculatedPoints = pointsOnBezierCurve(
          // t-values for the two anchor points on a cubic curve
          matrix([[settings.cubicSecondManipulationPointTValue], [settings.cubicThirdManipulationPointTValue]]),
          segmentControlPoints
        );
        const cubicSegment = [
          segmentControlPoints[0]!,
          calculatedPoints[0]!,
          calculatedPoints[1]!,
          segmentControlPoints[3]!,
        ];
        segmentsOfAnchorPoints.push(cubicSegment);
        break;
      }
      default:
        break;
    }
  }

  return segmentsOfAnchorPoints;
};

// --- Math for reversing the "touchable anchors" ---

/**
 * Solves for the P1 control point of a quadratic curve given
 * the start (P0), end (P2), and on-curve anchor (P_anchor at t=0.5)
 */
const quadControlPoint = (startPoint: Point, middleAnchorPoint: Point, endPoint: Point): Point => {
  // This is derived from solving the quadratic Bézier equation for P1:
  // P_anchor = (1-t)²*P0 + 2(1-t)t*P1 + t²*P2
  // For t=0.5:
  // P_anchor = 0.25*P0 + 0.5*P1 + 0.25*P2
  // 0.5*P1 = P_anchor - 0.25*P0 - 0.25*P2
  // P1 = 2*P_anchor - 0.5*P0 - 0.5*P2
  // P1 = 2*middleAnchorPoint - (startPoint + endPoint) / 2
  return {
    x: 2 * middleAnchorPoint.x - (startPoint.x + endPoint.x) / 2,
    y: 2 * middleAnchorPoint.y - (startPoint.y + endPoint.y) / 2,
  };
};

/*
 * 🧮 MATH: BÉZIER CURVE (BERNSTEIN POLYNOMIAL FORM)
 * These functions are the coefficients of the cubic Bézier equation in
 * its Bernstein polynomial form:
 * P(t) = P0*B0(t) + P1*B1(t) + P2*B2(t) + P3*B3(t)
 *
 * Where:
 * B0(t) = (1-t)³
 * B1(t) = 3(1-t)²t
 * B2(t) = 3(1-t)t²
 * B3(t) = t³
 *
 * These are used to solve for the unknown P1 and P2 control points.
 */
// B0(t) = (1-t)³ = 1 - 3t + 3t² - t³
const bernsteinCubicCoefficient0 = (tValue: number): number => {
  return 1 - 3 * tValue + 3 * Math.pow(tValue, 2) - Math.pow(tValue, 3);
};
// B1(t) = 3t(1-t)² = 3t - 6t² + 3t³
const bernsteinCubicCoefficient1 = (tValue: number): number => {
  return 3 * tValue - 6 * Math.pow(tValue, 2) + 3 * Math.pow(tValue, 3);
};
// B2(t) = 3t²(1-t) = 3t² - 3t³
const bernsteinCubicCoefficient2 = (tValue: number): number => {
  return 3 * Math.pow(tValue, 2) - 3 * Math.pow(tValue, 3);
};
// B3(t) = t³
const bernsteinCubicCoefficient3 = (tValue: number): number => {
  return Math.pow(tValue, 3);
};

/*
 * The following two functions (`cubicControlPoint1Coordinate` and
 * `cubicControlPoint2Coordinate`) are solving a 2x2 system of linear equations
 * derived from the Bernstein form.
 *
 * We have two known anchor points (A1, A2) at two known t-values (t1, t2):
 * A1 = P0*B0(t1) + P1*B1(t1) + P2*B2(t1) + P3*B3(t1)
 * A2 = P0*B0(t2) + P1*B1(t2) + P2*B2(t2) + P3*B3(t2)
 *
 * Since P0, P3, A1, A2, and all B(t) values are known, we can
 * rearrange this to solve for the two unknowns, P1 and P2.
 * These complex-looking formulas are the direct solution to that 2x2 system.
 * (t1 = cubicSecondManipulationPointTValue, t2 = cubicThirdManipulationPointTValue)
 */
const cubicControlPoint1Coordinate = (
  startPoint: number,
  firstMiddleAnchorPoint: number,
  secondMiddleAnchorPoint: number,
  endPoint: number
): number => {
  const b0 = bernsteinCubicCoefficient0(settings.cubicSecondManipulationPointTValue);
  const b1 = bernsteinCubicCoefficient1(settings.cubicSecondManipulationPointTValue);
  const b2 = bernsteinCubicCoefficient2(settings.cubicSecondManipulationPointTValue);
  const b3 = bernsteinCubicCoefficient3(settings.cubicSecondManipulationPointTValue);

  const returnValue =
    (b1 * firstMiddleAnchorPoint -
      b2 * secondMiddleAnchorPoint +
      (b2 * b3 - b0 * b1) * startPoint +
      (b2 * b0 - b1 * b3) * endPoint) /
    (b1 * b1 - b2 * b2);

  return returnValue;
};
const cubicControlPoint2Coordinate = (
  startPoint: number,
  firstMiddleAnchorPoint: number,
  secondMiddleAnchorPoint: number,
  endPoint: number
): number => {
  const b0 = bernsteinCubicCoefficient0(settings.cubicThirdManipulationPointTValue);
  const b1 = bernsteinCubicCoefficient1(settings.cubicThirdManipulationPointTValue);
  const b2 = bernsteinCubicCoefficient2(settings.cubicThirdManipulationPointTValue);
  const b3 = bernsteinCubicCoefficient3(settings.cubicThirdManipulationPointTValue);

  const returnValue =
    (b1 * firstMiddleAnchorPoint -
      b2 * secondMiddleAnchorPoint +
      (b2 * b0 - b3 * b1) * startPoint +
      (b2 * b3 - b1 * b0) * endPoint) /
    (b1 * b1 - b2 * b2);

  return returnValue;
};
const cubicControlPoint1 = (
  startPoint: Point,
  firstMiddleAnchorPoint: Point,
  secondMiddleAnchorPoint: Point,
  endPoint: Point
): Point => {
  return {
    x: cubicControlPoint1Coordinate(startPoint.x, firstMiddleAnchorPoint.x, secondMiddleAnchorPoint.x, endPoint.x),
    y: cubicControlPoint1Coordinate(startPoint.y, firstMiddleAnchorPoint.y, secondMiddleAnchorPoint.y, endPoint.y),
  };
};
const cubicControlPoint2 = (
  startPoint: Point,
  firstMiddleAnchorPoint: Point,
  secondMiddleAnchorPoint: Point,
  endPoint: Point
): Point => {
  return {
    x: cubicControlPoint2Coordinate(startPoint.x, firstMiddleAnchorPoint.x, secondMiddleAnchorPoint.x, endPoint.x),
    y: cubicControlPoint2Coordinate(startPoint.y, firstMiddleAnchorPoint.y, secondMiddleAnchorPoint.y, endPoint.y),
  };
};

/**
 * This takes a set of anchor points and calculates the off-curve
 * Bézier control points (P1, P2) needed to generate that exact curve.
 */
export const bezierPathsForTouchableAnchorPointPaths = (touchableAnchorPointPaths: Point[][]): Point[][] => {
  const segmentsOfControlPoints: Point[][] = [];

  for (let i = 0; i < touchableAnchorPointPaths.length; i++) {
    const anchorPoints = touchableAnchorPointPaths[i];
    switch (anchorPoints?.length) {
      case 2: // Linear
        segmentsOfControlPoints.push(anchorPoints);
        break;
      case 3: // Quadratic
        segmentsOfControlPoints.push([
          anchorPoints[0]!, // P0
          // Solve for P1
          quadControlPoint(anchorPoints[0]!, anchorPoints[1]!, anchorPoints[2]!),
          anchorPoints[2]!, // P2
        ]);
        break;
      case 4: // Cubic
        segmentsOfControlPoints.push([
          anchorPoints[0]!, // P0
          // Solve for P1
          cubicControlPoint1(anchorPoints[0]!, anchorPoints[1]!, anchorPoints[2]!, anchorPoints[3]!),
          // Solve for P2
          cubicControlPoint2(anchorPoints[0]!, anchorPoints[1]!, anchorPoints[2]!, anchorPoints[3]!),
          anchorPoints[3]!, // P3
        ]);
        break;
      default:
        break;
    }
  }
  return segmentsOfControlPoints;
};

/**
 * 🧮 MATH: LEAST-SQUARES FITTING (NORMAL EQUATION)
 * This function solves for the unknown control points that create the
 * "best fit" curve for a set of raw data points.
 *
 * It implements the **Normal Equation** for solving a linear least-squares
 * problem, which finds the vector `x` (unknown control points) that
 * minimizes the error in the equation `Ax = b`.
 *
 * The Normal Equation is: x = (A^T * A)^-1 * A^T * b
 *
 * In our case:
 * - `x` = `unknownControlPoints` (what we want to find)
 * - `A` = `multiply(timeMatrix, variableBezierMatrix)`
 * (The part of the Bézier matrix equation that multiplies the
 * *unknown* control points)
 * - `b` = `pointsToFit - multiply(timeMatrix, constantBezierMatrix) * fixedControlPoints`
 * (The raw points adjusted for the influence of the *known*
 * control points, i.e., the start and end points)
 */
const calculateControlPoints = ({
  pointsToFit,
  constantBezierMatrix,
  variableBezierMatrix,
  timeMatrix,
  fixedControlPoints,
}: {
  /**
   * `b` (partial)
   */
  pointsToFit: Matrix;
  /**
   * Part of `A`
   */
  constantBezierMatrix: Matrix;
  /**
   * Part of `A`
   */
  variableBezierMatrix: Matrix;
  /**
   * `T` matrix
   */
  timeMatrix: Matrix;
  /**
   * Known control points (P0, P3)
   */
  fixedControlPoints: Matrix;
}): Matrix => {
  // Let A = multiply(timeMatrix, variableBezierMatrix)
  // Let b_adj = pointsToFit - multiply(timeMatrix, constantBezierMatrix) * fixedControlPoints

  // tempMatrix = A^T
  const tempMatrix = transpose(multiply(timeMatrix, variableBezierMatrix));

  // x = (A^T * A)^-1 * (A^T * b_adj)
  const unknownControlPoints = multiply(
    // inv(A^T * A)
    inv(multiply(multiply(tempMatrix, timeMatrix), variableBezierMatrix)),
    // A^T * b_adj
    subtract(
      multiply(tempMatrix, pointsToFit),
      multiply(multiply(multiply(tempMatrix, timeMatrix), constantBezierMatrix), fixedControlPoints)
    )
  );

  return unknownControlPoints;
};

/**
 * 🎯 MAIN FUNCTION: FIT TOUCH POINTS TO BÉZIER CURVES
 * This is the primary function of the file. It takes an array of raw
 * touch points and attempts to fit them to the simplest possible
 * Bézier curve (Linear -> Quadratic -> Cubic).
 */
export const bezierPathsForTouchPoints = (points: Point[], useCuspDetection = false): Point[][] => {
  const firstPoint = points[0];
  const lastPoint = points[points.length - 1];

  if (!firstPoint || !lastPoint) {
    return [];
  }

  // --- Setup for Cubic Bézier (the most complex case) ---
  // These matrices split the full cubic Bézier matrix `m` into two parts:
  // m1: The part multiplied by *known* control points (P0, P3)
  // m2: The part multiplied by *unknown* control points (P1, P2)
  const m1 = matrix([
    [-1, 1],
    [3, 0],
    [-3, 0],
    [1, 0],
  ]);
  const m2 = matrix([
    [3, -3],
    [-6, 3],
    [3, 0],
    [0, 0],
  ]);

  // The full cubic Bézier basis matrix
  const m = matrix([
    [-1, 3, -3, 1],
    [3, -6, 3, 0],
    [-3, 3, 0, 0],
    [1, 0, 0, 0],
  ]);

  // Known points (start and end)
  const knownXControlPoints = matrix([[firstPoint.x], [lastPoint.x]]);
  const knownYControlPoints = matrix([[firstPoint.y], [lastPoint.y]]);

  // --- 1. Parameterization (Estimate 't' values) ---
  // We need to assign a 't' value (0.0 to 1.0) to each raw point.
  // A good estimate is to use the arc-length (distance along the path).

  const distanceMatrix = matrix(zeros([points.length, 1]));
  const yPointMatrix = matrix(zeros([points.length, 1]));
  const xPointMatrix = matrix(zeros([points.length, 1]));

  xPointMatrix.set([0, 0], firstPoint.x);
  yPointMatrix.set([0, 0], firstPoint.y);

  // Calculate cumulative distance for each point
  for (let i = 1; i < points.length; i++) {
    const currentPoint = points[i];
    const previousPoint = points[i - 1];

    if (!currentPoint || !previousPoint) {
      continue;
    }

    xPointMatrix.set([i, 0], currentPoint.x);
    yPointMatrix.set([i, 0], currentPoint.y);

    const distance = euclideanDistance(currentPoint, previousPoint);
    distanceMatrix.set([i, 0], distance);
  }

  let totalDistance = 0;
  for (let i = 0; i < points.length; i++) {
    totalDistance += distanceMatrix.get([i, 0]) as number;
  }

  // Safety check for co-incident points:
  if (totalDistance === 0) {
    return [[firstPoint, lastPoint]];
  }

  // Build the Time matrices (T) for linear, quadratic, and cubic
  // based on the 'distanceRatio' (our 't' value)
  const timeMatrix = matrix(zeros([points.length, 4])); // Cubic: [t³, t², t, 1]
  const linearTimeMatrix = matrix(zeros([points.length, 2])); // Linear: [t, 1]
  const quadraticTimeMatrix = matrix(zeros([points.length, 3])); // Quad: [t², t, 1]

  let currentDistance = 0;
  for (let i = 0; i < points.length; i++) {
    currentDistance += distanceMatrix.get([i, 0]) as number;
    const distanceRatio = currentDistance / totalDistance; // Our `t` value

    // Cubic T
    timeMatrix.set([i, 0], distanceRatio * distanceRatio * distanceRatio);
    timeMatrix.set([i, 1], distanceRatio * distanceRatio);
    timeMatrix.set([i, 2], distanceRatio);
    timeMatrix.set([i, 3], 1);

    // Quadratic T
    quadraticTimeMatrix.set([i, 0], distanceRatio * distanceRatio);
    quadraticTimeMatrix.set([i, 1], distanceRatio);
    quadraticTimeMatrix.set([i, 2], 1);

    // Linear T
    linearTimeMatrix.set([i, 0], distanceRatio);
    linearTimeMatrix.set([i, 1], 1);
  }

  // --- 2. Attempt Linear Fit ---
  const linearBezierMatrix = matrix([
    [-1, 1],
    [1, 0],
  ]);

  // Check the R² value for a simple line
  let R2 = rSquaredVal({
    xPointsToFit: xPointMatrix,
    yPointsToFit: yPointMatrix,
    yControlPoints: knownYControlPoints,
    xControlPoints: knownXControlPoints,
    bezierMatrix: linearBezierMatrix,
    timeMatrix: linearTimeMatrix,
  });

  // Use a different threshold for very short lines
  let linearThreshold = 0;
  if (euclideanDistance(firstPoint, lastPoint) < settings.smallLineDistanceThreshold) {
    linearThreshold = settings.linearErrorThresholdSmall;
  } else {
    linearThreshold = settings.linearErrorThreshold;
  }

  // If R² is high enough, it's a line. We're done.
  if (R2 > linearThreshold) {
    return [[firstPoint, lastPoint]];
  }

  // --- 3. Attempt Quadratic Fit ---

  // Setup matrices for quadratic least-squares
  const quadraticM1 = matrix([
    [1, 1],
    [-2, 0],
    [1, 0],
  ]); // Multiplies known points (P0, P2)
  const quadraticM2 = matrix([[-2], [2], [0]]); // Multiplies unknown point (P1)
  const quadBezierMatrix = matrix([
    [1, -2, 1],
    [-2, 2, 0],
    [1, 0, 0],
  ]);

  // Use the Normal Equation solver to find the single unknown
  // control point (P1) for X and Y.
  const xUnknownQuadPoint = calculateControlPoints({
    pointsToFit: xPointMatrix,
    constantBezierMatrix: quadraticM1,
    variableBezierMatrix: quadraticM2,
    timeMatrix: quadraticTimeMatrix,
    fixedControlPoints: knownXControlPoints,
  });
  const yUnknownQuadPoint = calculateControlPoints({
    pointsToFit: yPointMatrix,
    constantBezierMatrix: quadraticM1,
    variableBezierMatrix: quadraticM2,
    timeMatrix: quadraticTimeMatrix,
    fixedControlPoints: knownYControlPoints,
  });

  // Combine known and calculated points
  let yControlPoints = matrix([
    [knownYControlPoints.get([0, 0]) as number],
    [yUnknownQuadPoint.get([0, 0]) as number], // P1_y
    [knownYControlPoints.get([1, 0]) as number],
  ]);
  let xControlPoints = matrix([
    [knownXControlPoints.get([0, 0]) as number],
    [xUnknownQuadPoint.get([0, 0]) as number], // P1_x
    [knownXControlPoints.get([1, 0]) as number],
  ]);

  // Check the R² value for this quadratic fit
  R2 = rSquaredVal({
    xPointsToFit: xPointMatrix,
    yPointsToFit: yPointMatrix,
    yControlPoints,
    xControlPoints,
    bezierMatrix: quadBezierMatrix,
    timeMatrix: quadraticTimeMatrix,
  });

  // If R² is high enough, it's a quadratic curve. We're done.
  if (R2 > settings.quadErrorThreshold) {
    const controlPoints: Point[] = [
      firstPoint,
      {
        x: xControlPoints.get([1, 0]) as number,
        y: yControlPoints.get([1, 0]) as number,
      },
      lastPoint,
    ];

    return [controlPoints];
  }

  // --- 4. Attempt Cubic Fit ---
  // (If linear and quadratic fits failed)

  // Use the Normal Equation solver to find the *two* unknown
  // control points (P1, P2)
  let xUnknownControlPoints = calculateControlPoints({
    pointsToFit: xPointMatrix,
    constantBezierMatrix: m1,
    variableBezierMatrix: m2,
    timeMatrix,
    fixedControlPoints: knownXControlPoints,
  });
  let yUnknownControlPoints = calculateControlPoints({
    pointsToFit: yPointMatrix,
    constantBezierMatrix: m1,
    variableBezierMatrix: m2,
    timeMatrix,
    fixedControlPoints: knownYControlPoints,
  });

  // Combine known (P0, P3) and calculated (P1, P2) points
  yControlPoints = matrix([
    [knownYControlPoints.get([0, 0]) as number], // P0_y
    [yUnknownControlPoints.get([0, 0]) as number], // P1_y
    [yUnknownControlPoints.get([1, 0]) as number], // P2_y
    [knownYControlPoints.get([1, 0]) as number], // P3_y
  ]);
  xControlPoints = matrix([
    [knownXControlPoints.get([0, 0]) as number],
    [xUnknownControlPoints.get([0, 0]) as number],
    [xUnknownControlPoints.get([1, 0]) as number],
    [knownXControlPoints.get([1, 0]) as number],
  ]);

  const returnVector: Point[][] = [];
  const controlPoints: Point[] = [];

  for (let i = 0; i < 4; i++) {
    controlPoints.push({
      x: xControlPoints.get([i, 0]) as number,
      y: yControlPoints.get([i, 0]) as number,
    });
  }

  // --- 5. Iterative Refinement (for Cubic) ---
  // The initial 't' values were just estimates. Now that we have a
  // "pretty good" curve, we can re-estimate the 't' values for our
  // raw points by finding the *closest point on this new curve*.
  // This loop repeats the fit with progressively better 't' values.

  const tValues = matrix(zeros(settings.fittingPoints + 1, 1));
  for (let i = 0; i <= settings.fittingPoints; i++) {
    tValues.set([i, 0], i / settings.fittingPoints);
  }

  for (let numberOfRefactors = 0; numberOfRefactors < settings.fittingIterations; numberOfRefactors++) {
    // A. Generate high-resolution points on our *current best-fit* curve
    const pointsOnCurve = pointsOnBezierCurve(tValues, controlPoints);

    // B. Calculate arc-length distances for these new points
    const adjustedDistanceMatrix = matrix(zeros([settings.fittingPoints + 1, 1]));
    for (let i = 1; i <= settings.fittingPoints; i++) {
      const currentPoint = pointsOnCurve[i];
      const previousPoint = pointsOnCurve[i - 1];

      if (!currentPoint || !previousPoint) {
        continue;
      }

      const adjDistance = euclideanDistance(currentPoint, previousPoint);
      adjustedDistanceMatrix.set([i, 0], adjDistance);
    }

    let totalAdjustedDistance = 0;
    for (let i = 0; i < settings.fittingPoints; i++) {
      totalAdjustedDistance += adjustedDistanceMatrix.get([i, 0]);
    }

    // C. Re-parameterize: Find new 't' values for the *original* raw points
    //    by matching their distance ratio to the new curve's distance ratio.
    let currentDistanceIndex = 0;
    let matrixDistance = 0;
    const adjustedTimeMatrix = matrix(zeros([points.length, 4]));
    let pointDistance = 0;

    for (let i = 0; i < points.length; i++) {
      pointDistance += distanceMatrix.get([i, 0]); // Distance on original path

      // Find the point on the *new curve* that has the same
      // proportional distance
      while (
        pointDistance / totalDistance >= matrixDistance / totalAdjustedDistance &&
        currentDistanceIndex < adjustedDistanceMatrix.size()[0]!
      ) {
        matrixDistance += adjustedDistanceMatrix.get([currentDistanceIndex, 0]);
        currentDistanceIndex++;
      }

      // Get the 't' value from that "closest" point
      let adjustedTVal = (currentDistanceIndex - 1) * (1 / settings.fittingPoints);
      if (i === points.length - 1) {
        adjustedTVal = 1;
      }

      // Build a new Time Matrix (T) using these *better* t-values
      adjustedTimeMatrix.set([i, 0], Math.pow(adjustedTVal, 3));
      adjustedTimeMatrix.set([i, 1], Math.pow(adjustedTVal, 2));
      adjustedTimeMatrix.set([i, 2], adjustedTVal);
      adjustedTimeMatrix.set([i, 3], 1);
    }

    // D. Re-run the least-squares fit using the new, improved Time Matrix
    xUnknownControlPoints = calculateControlPoints({
      pointsToFit: xPointMatrix,
      constantBezierMatrix: m1,
      variableBezierMatrix: m2,
      timeMatrix: adjustedTimeMatrix,
      fixedControlPoints: knownXControlPoints,
    });
    yUnknownControlPoints = calculateControlPoints({
      pointsToFit: yPointMatrix,
      constantBezierMatrix: m1,
      variableBezierMatrix: m2,
      timeMatrix: adjustedTimeMatrix,
      fixedControlPoints: knownYControlPoints,
    });

    // E. Update the control points for the next iteration
    yControlPoints = matrix([
      [knownYControlPoints.get([0, 0]) as number],
      [yUnknownControlPoints.get([0, 0]) as number],
      [yUnknownControlPoints.get([1, 0]) as number],
      [knownYControlPoints.get([1, 0]) as number],
    ]);
    xControlPoints = matrix([
      [knownXControlPoints.get([0, 0]) as number],
      [xUnknownControlPoints.get([0, 0]) as number],
      [xUnknownControlPoints.get([1, 0]) as number],
      [knownXControlPoints.get([1, 0]) as number],
    ]);

    for (let i = 0; i < 4; i++) {
      controlPoints[i] = {
        x: xControlPoints.get([i, 0]) as number,
        y: yControlPoints.get([i, 0]) as number,
      };
    }
  }
  // --- End of Iterative Refinement ---

  // Check the R² of the final, refined cubic curve
  R2 = rSquaredVal({
    xPointsToFit: xPointMatrix,
    yPointsToFit: yPointMatrix,
    yControlPoints,
    xControlPoints,
    bezierMatrix: m,
    timeMatrix, // Use the original time matrix for the final check
  });

  // --- 6. Cusp Detection (Optional) ---
  // If the fit is good, check if it's an "S" curve (a cusp).
  // This logic tries to find the raw point closest to P1 and P2.
  // If it can split the curve at one of these points, it will
  // recursively call itself on the two halves.
  if (R2 >= settings.cubicErrorThreshold && useCuspDetection) {
    // This part is complex: it finds the raw point nearest to the
    // new control points (P1, P2) and tries to split the
    // point array there, then re-runs the *entire* fitting process
    // on the two new sub-arrays.

    let minC1Distance = 100_000;
    let minC1DistanceIndex = 1_000_000;
    let minC2Distance = 100_000;
    let minC2DistanceIndex = 1_000_000;
    for (let i = 0; i < points.length; i++) {
      const currDistanceC1 = euclideanDistance(controlPoints[1]!, points[i]!);
      const currDistanceC2 = euclideanDistance(controlPoints[2]!, points[i]!);
      if (currDistanceC1 < minC1Distance) {
        minC1Distance = currDistanceC1;
        minC1DistanceIndex = i;
      }
      if (currDistanceC2 < minC2Distance) {
        minC2Distance = currDistanceC2;
        minC2DistanceIndex = i;
      }
    }

    const c1SplitIndex = minC1DistanceIndex;
    const splitC1Lo = points.slice(0, c1SplitIndex + 1);
    const splitC1Hi = points.slice(c1SplitIndex);

    const c2SplitIndex = minC2DistanceIndex;
    const splitC2Lo = points.slice(0, c2SplitIndex + 1);
    const splitC2Hi = points.slice(c2SplitIndex);

    if (splitC1Lo.length > 3 && splitC1Hi.length > 3) {
      let splitLoBezierPaths = bezierPathsForTouchPoints(splitC1Lo, false);
      let splitHiBezierPaths = bezierPathsForTouchPoints(splitC1Hi, false);
      if (splitHiBezierPaths[0]?.length === 2 || splitLoBezierPaths[0]?.length === 2) {
        for (const p of splitLoBezierPaths) {
          returnVector.push(p);
        }
        for (const p of splitHiBezierPaths) {
          returnVector.push(p);
        }
      } else if (splitC2Lo.length > 3 && splitC2Hi.length > 3) {
        splitLoBezierPaths = bezierPathsForTouchPoints(splitC2Lo, false);
        splitHiBezierPaths = bezierPathsForTouchPoints(splitC2Hi, false);
        if (splitHiBezierPaths[0]?.length === 2 || splitLoBezierPaths[0]?.length === 2) {
          for (const p of splitLoBezierPaths) {
            returnVector.push(p);
          }
          for (const p of splitHiBezierPaths) {
            returnVector.push(p);
          }
        } else {
          returnVector.push(controlPoints);
        }
      } else {
        returnVector.push(controlPoints);
      }
    } else {
      returnVector.push(controlPoints);
    }
  } else {
    // If not using cusp detection, or if R² is too low,
    // just return the single cubic curve we found.
    returnVector.push(controlPoints);
  }

  return returnVector;
};
